/**
 * Service for requesting IBE decryption keys from the backend server
 * Uses HTTP/2 (via fetch API, which automatically uses HTTP/2 when available)
 */

import { ethers } from 'ethers';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { Buffer } from 'buffer';
import { db } from '../firebase/config';

/** Add common string variants of a tx / request id for exact `k` path matching. */
function addTxHashVariants(targetSet, h) {
  if (typeof h !== 'string' || h.length === 0) return;
  targetSet.add(h);
  targetSet.add(h.toLowerCase());
  if (h.startsWith('0x')) {
    targetSet.add(h.slice(2));
    targetSet.add(h.slice(2).toLowerCase());
  } else {
    targetSet.add(`0x${h}`);
    targetSet.add(`0x${h.toLowerCase()}`);
  }
}

/**
 * Request IBE decryption key from the backend server
 * @param {string} userAddress - User's wallet address
 * @param {string} chainKey - Chain identifier (e.g., 'ethereum', 'arbitrum')
 * @param {string} transactionId - Optional transaction ID for context
 * @returns {Promise<string>} IBE private key as a string
 */
export const requestIbeDecryptionKey = async (userAddress, chainKey, transactionId = null) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.example.com';
  
  if (!userAddress) {
    throw new Error('User address is required');
  }
  
  if (!chainKey) {
    throw new Error('Chain key is required');
  }

  try {
    // Prepare request payload
    const payload = {
      userAddress: userAddress.toLowerCase(),
      chainKey: chainKey.toLowerCase(),
      ...(transactionId && { transactionId })
    };

    // Make HTTP/2 request to backend server
    // Note: fetch API automatically uses HTTP/2 when the server supports it
    const response = await fetch(`${backendUrl}/api/v1/keys/ibe-decryption`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        // Add any authentication headers if needed
        // 'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(payload),
      // HTTP/2 is automatically used if the server supports it
      // No explicit HTTP/2 configuration needed in browser fetch API
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        message: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(errorData.message || `Failed to request decryption key: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.key) {
      throw new Error('Backend response missing decryption key');
    }

    return data.key;
  } catch (error) {
    // Handle network errors, timeout, etc.
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to backend server');
    }
    throw error;
  }
};

/**
 * Validate IBE decryption key format (optional helper)
 * @param {string} key - IBE private key to validate
 * @returns {boolean} True if key appears valid
 */
export const validateIbeKey = (key) => {
  if (!key || typeof key !== 'string') {
    return false;
  }
  // Basic validation - adjust based on your IBE key format
  return key.length > 0;
};

/**
 * Derive Ethereum address from SECP256k1 public key
 * Supports both base64 (new format) and hex (legacy format) public keys
 * @param {string} secpPublicKey - SECP256k1 public key (base64 or hex string)
 * @returns {string} Ethereum address
 */
export const deriveEthereumAddress = (secpPublicKey) => {
  if (!secpPublicKey) {
    throw new Error('SECP public key is required');
  }
  
  try {
    const cleanKey = secpPublicKey.trim();
    let publicKeyBytes;
    
    // Determine if it's base64 or hex
    // Strategy: 
    // 1. If starts with 0x, it's definitely hex
    // 2. If it's a short string (<= 130 chars) and only hex chars, it's hex
    // 3. Otherwise, if it matches base64 pattern and is long enough, it's base64
    const base64Regex = /^[A-Za-z0-9+/]+=*$/;
    const hexOnlyRegex = /^[0-9a-fA-F]+$/i;
    
    const startsWith0x = cleanKey.startsWith('0x');
    const isHexOnly = hexOnlyRegex.test(cleanKey);
    const isBase64Like = base64Regex.test(cleanKey);
    
    // Hex keys are typically 66, 128, or 130 hex characters (with or without 0x)
    // Base64 keys for 65 bytes are typically 88 characters
    const isHex = startsWith0x || (isHexOnly && cleanKey.length <= 132);
    const isBase64 = !isHex && isBase64Like && cleanKey.length >= 80;
    
    if (isBase64) {
      // Decode base64 to bytes
      try {
        publicKeyBytes = Buffer.from(cleanKey, 'base64');
        if (process.env.NODE_ENV === 'development') {
          console.log('[deriveEthereumAddress] Decoded base64 key:', {
            originalLength: cleanKey.length,
            decodedBytes: publicKeyBytes.length,
            firstByte: publicKeyBytes[0]
          });
        }
      } catch (e) {
        throw new Error(`Failed to decode base64: ${e.message}`);
      }
    } else if (isHex) {
      // Parse hex string to bytes
      const hexKey = cleanKey.startsWith('0x') ? cleanKey.slice(2) : cleanKey;
      if (!hexOnlyRegex.test(hexKey)) {
        throw new Error('Invalid hex string');
      }
      publicKeyBytes = Buffer.from(hexKey, 'hex');
      if (process.env.NODE_ENV === 'development') {
        console.log('[deriveEthereumAddress] Parsed hex key:', {
          hexLength: hexKey.length,
          decodedBytes: publicKeyBytes.length,
          firstByte: publicKeyBytes[0]
        });
      }
    } else {
      throw new Error(`Public key format not recognized. Must be base64 (${cleanKey.length} chars) or hex format.`);
    }
    
    // Normalize to format ethers expects
    // ethers.utils.computeAddress expects either:
    // - 64 bytes uncompressed (X: 32 bytes + Y: 32 bytes) = 128 hex chars
    // - 33 bytes compressed = 66 hex chars
    let normalizedBytes;
    
    if (publicKeyBytes.length === 65) {
      if (publicKeyBytes[0] === 0x04) {
        // Uncompressed with 0x04 prefix: remove prefix to get 64 bytes
        normalizedBytes = publicKeyBytes.slice(1);
        if (process.env.NODE_ENV === 'development') {
          console.log('[deriveEthereumAddress] Removed 0x04 prefix from 65 bytes, normalized to 64 bytes');
        }
      } else {
        // 65 bytes but doesn't start with 0x04 - might be malformed
        // Try removing first byte anyway to get 64 bytes
        normalizedBytes = publicKeyBytes.slice(1);
        if (process.env.NODE_ENV === 'development') {
          console.warn('[deriveEthereumAddress] 65 bytes without 0x04 prefix, removing first byte');
        }
      }
    } else if (publicKeyBytes.length === 64) {
      // Already 64 bytes uncompressed (no prefix)
      normalizedBytes = publicKeyBytes;
      if (process.env.NODE_ENV === 'development') {
        console.log('[deriveEthereumAddress] Using 64 bytes as-is');
      }
    } else if (publicKeyBytes.length === 33) {
      // Compressed format (33 bytes) - ethers can handle this
      normalizedBytes = publicKeyBytes;
      if (process.env.NODE_ENV === 'development') {
        console.log('[deriveEthereumAddress] Using compressed 33 bytes');
      }
    } else if (publicKeyBytes.length > 65) {
      // Longer than expected - try to extract valid key
      if (publicKeyBytes[0] === 0x04) {
        // Has 0x04 prefix, extract 64 bytes after it
        normalizedBytes = publicKeyBytes.slice(1, 65);
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[deriveEthereumAddress] Key longer than expected (${publicKeyBytes.length} bytes), extracted 64 bytes after 0x04`);
        }
      } else {
        // No 0x04 prefix, take first 64 bytes
        normalizedBytes = publicKeyBytes.slice(0, 64);
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[deriveEthereumAddress] Key longer than expected (${publicKeyBytes.length} bytes), using first 64 bytes`);
        }
      }
    } else {
      throw new Error(
        `Invalid public key length: ${publicKeyBytes.length} bytes. ` +
        `Expected 33 (compressed), 64 (uncompressed), or 65 (uncompressed with 0x04 prefix) bytes. ` +
        `Got ${publicKeyBytes.length} bytes.`
      );
    }
    
    // Convert to hex string with 0x prefix for ethers
    const hexString = normalizedBytes.toString('hex');
    const publicKeyHex = '0x' + hexString;
    
    // Validate final hex length
    const hexLength = hexString.length;
    if (hexLength !== 128 && hexLength !== 66) {
      throw new Error(
        `Invalid normalized hex length: ${hexLength} hex chars. ` +
        `Expected 66 (compressed) or 128 (uncompressed) hex chars. ` +
        `Got ${hexLength} hex chars. ` +
        `Normalized from ${publicKeyBytes.length} bytes.`
      );
    }
    
    // Validate hex string format
    if (!/^[0-9a-f]+$/i.test(hexString)) {
      throw new Error(`Invalid hex string format after conversion`);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[deriveEthereumAddress] Final hex key:', {
        hexLength,
        hexPreview: publicKeyHex.substring(0, 20) + '...',
        normalizedFromBytes: normalizedBytes.length
      });
    }
    
    // Compute Ethereum address
    // ethers.utils.computeAddress expects:
    // - Compressed: 0x + 66 hex chars (33 bytes)
    // - Uncompressed: 0x + 128 hex chars (64 bytes)
    try {
      const address = ethers.utils.computeAddress(publicKeyHex);
      return address.toLowerCase();
    } catch (ethersError) {
      // Provide more context about what we're passing to ethers
      throw new Error(
        `ethers.computeAddress failed: ${ethersError.message}. ` +
        `Hex length: ${hexLength}, Format: ${hexLength === 66 ? 'compressed' : 'uncompressed'}`
      );
    }
  } catch (error) {
    // Provide more context in error message
    const keyPreview = secpPublicKey ? (secpPublicKey.substring(0, 30) + '...') : 'null';
    const errorMsg = error.message || String(error);
    throw new Error(`Failed to derive Ethereum address from public key (${keyPreview}): ${errorMsg}`);
  }
};

/**
 * Normalize an IBE master public key for equality checks.
 * @param {string|null|undefined} pubkey
 * @returns {string}
 */
export const normalizeMasterPubkey = (pubkey) => {
  if (typeof pubkey !== 'string') return '';
  return pubkey.trim().toLowerCase().replace(/^0x/, '');
};

/**
 * Fetch the currently active IBE master public key from the PEP API.
 * Response shape (protojson): `{ active_pubkey: { public_key, creator, expiry }, queued_pubkey?: … }`
 * @returns {Promise<string|null>} Active master public key hex/string, or null if unavailable
 */
export const fetchActiveMasterPubkey = async () => {
  const serverUrl = process.env.REACT_APP_PEP_SERVER_URL || '';
  if (!serverUrl) {
    console.warn('[fetchActiveMasterPubkey] REACT_APP_PEP_SERVER_URL is not set');
    return null;
  }

  try {
    const response = await fetch(`${serverUrl}/v1/pep/pubkey`, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) {
      console.warn('[fetchActiveMasterPubkey] HTTP error:', response.status, response.statusText);
      return null;
    }
    const data = await response.json();
    const active =
      data?.active_pubkey?.public_key ??
      data?.active_pubkey?.publicKey ??
      data?.activePubkey?.public_key ??
      data?.activePubkey?.publicKey ??
      null;
    if (typeof active === 'string' && active.trim() !== '') {
      console.log('[fetchActiveMasterPubkey] Active master pubkey:', {
        preview: `${active.substring(0, 20)}…`,
        length: active.length
      });
      return active.trim();
    }
    console.warn('[fetchActiveMasterPubkey] Response missing active_pubkey.public_key:', data);
    return null;
  } catch (error) {
    console.warn('[fetchActiveMasterPubkey] Failed:', error?.message || error);
    return null;
  }
};

/**
 * Extract the master public key that a stored decryption-key kv entry was issued under.
 * Comes from PrivateRequest.pubkey in the chain response (or top-level pubkey).
 * @param {Object|null} kvPayload - Result from checkExistingKeyRequest (or its `.value`)
 * @returns {string|null}
 */
export const extractMasterPubkeyFromKvEntry = (kvPayload) => {
  if (!kvPayload) return null;

  const value = kvPayload.value !== undefined ? kvPayload.value : kvPayload;
  const candidates = [];

  const pushPubkey = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    const pk = obj.pubkey ?? obj.Pubkey ?? obj.public_key ?? obj.publicKey;
    if (typeof pk === 'string' && pk.trim() !== '') candidates.push(pk.trim());
  };

  pushPubkey(value);

  let chainResp = value?.chain_response ?? value?.ChainResponse;
  if (typeof chainResp === 'string') {
    try {
      chainResp = JSON.parse(chainResp);
    } catch {
      chainResp = null;
    }
  }
  pushPubkey(chainResp);

  const dkSuccess = getDkRequestSuccessFromPayload(value, kvPayload);
  if (dkSuccess && dkSuccess !== value) {
    pushPubkey(dkSuccess);
    let nested = dkSuccess.chain_response ?? dkSuccess.ChainResponse;
    if (typeof nested === 'string') {
      try {
        nested = JSON.parse(nested);
      } catch {
        nested = null;
      }
    }
    pushPubkey(nested);
  }

  return candidates[0] || null;
};

/**
 * Extract succeeded_at timestamp from a kv success entry.
 * @param {Object|null} kvPayload
 * @returns {string|null} RFC3339 string or null
 */
export const extractSucceededAtFromKvEntry = (kvPayload) => {
  if (!kvPayload) return null;
  const value = kvPayload.value !== undefined ? kvPayload.value : kvPayload;
  const ts = value?.succeeded_at ?? value?.SucceededAt;
  return typeof ts === 'string' && ts.trim() !== '' ? ts.trim() : null;
};

/**
 * Whether a kv entry is usable given optional freshness filters.
 * Used after master-pubkey rotation so we don't reuse a key issued under an old MPK.
 * @param {Object|null} kvPayload
 * @param {Object} [filters]
 * @param {string|null} [filters.requiredMasterPubkey] - Current on-chain active master pubkey
 * @param {string|null} [filters.minSucceededAt] - Only accept entries with succeeded_at strictly after this ISO time
 * @returns {boolean}
 */
export const kvEntryMatchesFilters = (kvPayload, filters = {}) => {
  const { requiredMasterPubkey = null, minSucceededAt = null } = filters;

  if (requiredMasterPubkey) {
    const stored = extractMasterPubkeyFromKvEntry(kvPayload);
    if (stored) {
      if (normalizeMasterPubkey(stored) !== normalizeMasterPubkey(requiredMasterPubkey)) {
        console.log('[kvEntryMatchesFilters] Stale key: master pubkey mismatch', {
          storedPreview: `${stored.substring(0, 20)}…`,
          requiredPreview: `${String(requiredMasterPubkey).substring(0, 20)}…`
        });
        return false;
      }
    } else {
      // Legacy / incomplete entries: allow reuse. Decryption failure paths force a
      // fresh key request when the cached key no longer works after MPK rotation.
      console.log('[kvEntryMatchesFilters] No stored master pubkey on kv entry; allowing with requiredMasterPubkey set');
    }
  }

  if (minSucceededAt) {
    const succeededAt = extractSucceededAtFromKvEntry(kvPayload);
    if (!succeededAt) {
      console.log('[kvEntryMatchesFilters] Rejecting entry without succeeded_at (minSucceededAt set)');
      return false;
    }
    const entryMs = Date.parse(succeededAt);
    const minMs = Date.parse(minSucceededAt);
    if (Number.isNaN(entryMs) || Number.isNaN(minMs) || entryMs <= minMs) {
      console.log('[kvEntryMatchesFilters] Entry not newer than minSucceededAt', {
        succeededAt,
        minSucceededAt
      });
      return false;
    }
  }

  return true;
};

/**
 * Check Firestore for existing decryption key request
 * Keys are stored in the kv collection with k field containing paths like:
 * - dkreq/success/{auditorIdentity}/{userAddress}
 * - dkreq/success/{auditorIdentity}/{userAddress}/{txHashOrRequestId}  (5-segment form)
 * - dkreq/dlq/{auditorIdentity}/{userAddress}
 * (dkreq/pending/ is not ready — ignore until it becomes success or dlq)
 * @param {string} auditorIdentity - Auditor identity (UID) for key request lookups
 * @param {string} identity - IBE / PEP identity (address.toLowerCase() + chain pk, or legacy wallet address)
 * @param {Object} [options]
 * @param {string|null} [options.txHash] - PEP `tx_hash` (may differ from on-chain hash)
 * @param {string|null} [options.chainTxHash] - On-chain transaction hash from the dashboard row
 * @param {string[]} [options.extraTxHashes] - Any other ids the backend might append as a 5th path segment
 * @param {string[]} [options.extraAuditorIds] - e.g. `grantId`, Firestore doc id, if they differ from `auditorIdentity`
 * @param {string|null} [options.requestId] - Optional opaque request / row id used as 5th segment by some backends
 * @param {boolean} [options.fast=false] - Skip expensive encoded-doc probes (use while polling)
 * @param {string|null} [options.requiredMasterPubkey] - Skip keys issued under a different IBE master pubkey
 * @param {string|null} [options.minSucceededAt] - Skip keys whose succeeded_at is not strictly after this ISO time
 * @returns {Promise<Object|null>} The existing key request data or null if not found
 */
export const checkExistingKeyRequest = async (auditorIdentity, identity, options = {}) => {
  try {
    const {
      txHash = null,
      chainTxHash = null,
      extraTxHashes = [],
      extraAuditorIds = [],
      requestId = null,
      fast = false,
      requiredMasterPubkey = null,
      minSucceededAt = null
    } = options;
    // Firebase Auth UIDs are case-sensitive on the wire but the Firestore rules
    // match k case-insensitively. We compare client-side case-insensitively below
    // so callers can pass either the original-case or lowercased identity.
    const lowerAuditor = auditorIdentity.toLowerCase();
    const lowerIdentity = identity.toLowerCase();

    // Match k: exact 4-segment path OR 5-segment path (e.g. .../userAddress/txHash),
    // case-insensitive — the rules and backend treat the auditor identity that way.
    const kMatchesPattern = (kValue, pattern) => {
      if (!kValue || typeof kValue !== 'string') return false;
      const k = kValue.toLowerCase();
      const p = pattern.toLowerCase();
      return k === p || k.startsWith(p + '/');
    };

    // Only consider ready entries: success or dlq. Ignore pending (not ready yet).
    const statuses = ['success', 'dlq'];

    // Build path prefixes for client-side matching (case-insensitive on `k`).
    // With relaxed `kv` rules (`allow read: if isAuditor()`), we load the whole
    // collection once and filter — same as the legacy dashboard behavior.
    const auditorVariants = new Set([lowerAuditor, auditorIdentity]);
    for (const id of extraAuditorIds) {
      if (typeof id === 'string' && id.length > 0) {
        auditorVariants.add(id);
        auditorVariants.add(id.toLowerCase());
      }
    }
    try {
      const uid = getAuth().currentUser?.uid;
      if (typeof uid === 'string' && uid.length > 0) {
        auditorVariants.add(uid);
        auditorVariants.add(uid.toLowerCase());
      }
    } catch (_) {
      // ignore
    }

    const txVariants = new Set();
    addTxHashVariants(txVariants, txHash);
    addTxHashVariants(txVariants, chainTxHash);
    for (const h of extraTxHashes) {
      addTxHashVariants(txVariants, h);
    }
    if (typeof requestId === 'string' && requestId.length > 0) {
      txVariants.add(requestId);
      txVariants.add(requestId.toLowerCase());
    }

    const searchPatterns = [];
    for (const status of statuses) {
      for (const aud of auditorVariants) {
        searchPatterns.push(`dkreq/${status}/${aud}/${lowerIdentity}`);
        for (const tx of txVariants) {
          searchPatterns.push(`dkreq/${status}/${aud}/${lowerIdentity}/${tx}`);
        }
      }
    }
    const uniquePatterns = Array.from(new Set(searchPatterns));

    const freshnessFilters = { requiredMasterPubkey, minSucceededAt };

    console.log('[checkExistingKeyRequest] Searching for existing key in kv collection:', {
      auditorIdentity: lowerAuditor,
      userIdentity: lowerIdentity,
      txHash,
      chainTxHash,
      extraTxHashes,
      extraAuditorIds,
      requestId,
      requiredMasterPubkey: requiredMasterPubkey
        ? `${String(requiredMasterPubkey).substring(0, 20)}…`
        : null,
      minSucceededAt,
      patternCount: uniquePatterns.length,
      searchPatterns: uniquePatterns,
      rawAuditorIdentity: auditorIdentity,
      rawIdentity: identity
    });

    const kvCollection = collection(db, 'kv');

    const docToResult = (docSnap) => {
      const data = docSnap.data();
      let value = data.v || data.value || data;
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch (e) {
          // Keep as string
        }
      }
      return {
        key: data.k || docSnap.id,
        value,
        _byteString: data._byteString,
        docId: docSnap.id
      };
    };

    const acceptResult = (result, via) => {
      if (!result) return null;
      if (!kvEntryMatchesFilters(result, freshnessFilters)) {
        console.log('[checkExistingKeyRequest] Path matched but entry rejected by freshness filters:', {
          via,
          docId: result.docId,
          k: result.key
        });
        return null;
      }
      console.log('[checkExistingKeyRequest] Match accepted:', {
        via,
        docId: result.docId,
        k: result.key
      });
      return result;
    };

    // Strategy 1 (primary): read entire `kv` collection and match `k` in memory.
    // Matches legacy behavior; works when rules allow auditors to list `kv`.
    try {
      const allKvDocs = await getDocs(kvCollection);
      console.log('[checkExistingKeyRequest] Scanned kv collection:', { totalDocs: allKvDocs.size });
      for (const docSnap of allKvDocs.docs) {
        const kValue = docSnap.data()?.k;
        if (!uniquePatterns.some((pattern) => kMatchesPattern(kValue, pattern))) continue;
        const accepted = acceptResult(docToResult(docSnap), 'full collection scan');
        if (accepted) return accepted;
      }
    } catch (scanErr) {
      console.warn('[checkExistingKeyRequest] Full kv collection scan failed:', scanErr.message);
    }

    // Strategy 2: filtered `where('k', 'in', …)` (strict rules / large collections).
    const IN_CHUNK = 30;
    try {
      for (let i = 0; i < uniquePatterns.length; i += IN_CHUNK) {
        const chunk = uniquePatterns.slice(i, i + IN_CHUNK);
        const inQuery = query(kvCollection, where('k', 'in', chunk));
        const snap = await getDocs(inQuery);
        for (const docSnap of snap.docs) {
          const data = docSnap.data();
          if (!uniquePatterns.some((pattern) => kMatchesPattern(data.k, pattern))) continue;
          const accepted = acceptResult(docToResult(docSnap), 'where("k","in",…)');
          if (accepted) return accepted;
        }
      }
    } catch (inErr) {
      console.warn('[checkExistingKeyRequest] where("k","in",…) query failed:', inErr.message);
    }

    // Strategy 2b: prefix range on `k` so we find 5-segment keys without knowing tx_hash.
    // Matches `dkreq/success|{dlq}/{aud}/{user}` and `.../{user}/{anything}`.
    try {
      const prefixBases = [];
      for (const status of statuses) {
        for (const aud of auditorVariants) {
          prefixBases.push(`dkreq/${status}/${aud}/${lowerIdentity}`);
        }
      }
      for (const prefix of Array.from(new Set(prefixBases))) {
        const rangeQuery = query(
          kvCollection,
          where('k', '>=', prefix),
          where('k', '<=', `${prefix}\uf8ff`)
        );
        const snap = await getDocs(rangeQuery);
        for (const docSnap of snap.docs) {
          const kValue = docSnap.data()?.k;
          const pathOk = typeof kValue === 'string' && (
            kValue === prefix ||
            kValue.startsWith(`${prefix}/`) ||
            kValue.toLowerCase() === prefix.toLowerCase() ||
            kValue.toLowerCase().startsWith(`${prefix.toLowerCase()}/`)
          );
          if (!pathOk) continue;
          const accepted = acceptResult(docToResult(docSnap), 'k prefix range');
          if (accepted) return accepted;
        }
      }
    } catch (rangeErr) {
      console.warn('[checkExistingKeyRequest] k prefix range query failed:', rangeErr.message);
    }

    // Strategy 3: encoded document IDs (slow — skipped while polling).
    if (!fast) {
      const encodedLookupPatterns = [...uniquePatterns]
        .sort((a, b) => {
          const ca = (a.match(/\//g) || []).length;
          const cb = (b.match(/\//g) || []).length;
          return ca - cb;
        })
        .slice(0, 48);

      const encodedDocumentIdsForPattern = (pattern) => {
        const standard = Buffer.from(pattern).toString('base64');
        const noPadding = standard.replace(/=+$/g, '');
        const urlSafe = noPadding.replace(/\+/g, '-').replace(/\//g, '_');
        const urlSafePadded = standard.replace(/\+/g, '-').replace(/\//g, '_');
        return Array.from(new Set([standard, noPadding, urlSafe, urlSafePadded]));
      };

      for (const pattern of encodedLookupPatterns) {
        for (const encodedId of encodedDocumentIdsForPattern(pattern)) {
          try {
            const docRef = doc(db, 'kv', encodedId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              const data = docSnap.data();
              if (data.k && !kMatchesPattern(data.k, pattern)) {
                continue;
              }
              const accepted = acceptResult(docToResult(docSnap), 'encoded doc id');
              if (accepted) return accepted;
            }
          } catch (encodeError) {
            console.warn(`[checkExistingKeyRequest] Encoded ID lookup failed for "${pattern}" as "${encodedId}":`, encodeError.message);
          }
        }
      }
    }

    console.log('[checkExistingKeyRequest] No matching document found in kv collection');
    return null;
  } catch (error) {
    console.error('[checkExistingKeyRequest] Error checking existing key request:', {
      error: error.message,
      stack: error.stack,
      auditorIdentity,
      identity
    });
    // Don't throw - if check fails, proceed with new request
    return null;
  }
};

/**
 * Request private decryption key from PEP API
 * @param {string} auditorIdentity - Auditor identity (UID) for key request lookups
 * @param {string} identity - IBE / PEP identity (address.toLowerCase() + chain pk RandomnessId, or legacy wallet address)
 * @param {string} secpPublicKey - Auditor's SECP256k1 public key
 * @returns {Promise<Object>} Response with tx_hash and status
 */
export const requestPrivateDecryptionKey = async (auditorIdentity, identity, secpPublicKey, options = {}) => {
  const serverUrl = process.env.REACT_APP_PEP_SERVER_URL || '';
  const { timeoutMs = 20000, signal = null } = options;
  
  if (!auditorIdentity) {
    throw new Error('Auditor identity is required');
  }
  
  if (!identity) {
    throw new Error('Identity is required');
  }
  
  if (!secpPublicKey) {
    throw new Error('SECP public key is required');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const onOuterAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) {
      clearTimeout(timeoutId);
      throw new Error('Key request aborted');
    }
    signal.addEventListener('abort', onOuterAbort, { once: true });
  }

  try {
    const payload = {
      auditor_identity: auditorIdentity.toLowerCase(),
      identity: identity.toLowerCase(),
      secp_pubkey: secpPublicKey
    };

    const response = await fetch(`${serverUrl}/v1/pep/request-private-decryption-key`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        message: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(errorData.message || `Failed to request decryption key: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.tx_hash || data.status !== 'success') {
      throw new Error('Backend response missing required fields or status is not success');
    }

    return data;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Network error: Unable to connect to compliance server');
    }
    // Handle network errors, timeout, etc.
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to compliance server');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    if (signal) {
      signal.removeEventListener('abort', onOuterAbort);
    }
  }
};

/**
 * True when `fetch` failed in a way that hides the response but does not prove the
 * request was rejected — a missing CORS header on the compliance server's reply,
 * or a dropped connection. In both cases the request may well have been executed,
 * so the key can still land in `kv` and should be polled for.
 */
const isUnreadableResponseError = (error) => {
  const message = error && typeof error.message === 'string' ? error.message : '';
  return /Network error: Unable to connect to compliance server|Failed to fetch|NetworkError|CORS/i.test(
    message
  );
};

/**
 * Poll Firestore for decryption key until it's ready or timeout
 * @param {string} auditorIdentity - Auditor identity (UID) for key request lookups
 * @param {string} identity - User's wallet address
 * @param {Object} options - Polling options
 * @param {number} options.interval - Polling interval in milliseconds (default: 4000)
 * @param {number} options.timeout - Maximum time to poll in milliseconds (default: 300000)
 * @param {Function} options.onPoll - Callback called on each poll attempt
 * @returns {Promise<Object>} The key request data when found
 */
export const pollForDecryptionKey = async (auditorIdentity, identity, options = {}) => {
  const {
    interval = 4000,
    timeout = 300000, // 5 minutes — first-time keygen / PEP delivery can exceed 2 minutes
    onPoll = null,
    abortSignal = null,
    txHash = null,
    chainTxHash = null,
    extraTxHashes = [],
    extraAuditorIds = [],
    requestId = null,
    fast = true,
    requiredMasterPubkey = null,
    minSucceededAt = null
  } = options;

  const lookupOptions = {
    txHash,
    chainTxHash,
    extraTxHashes,
    extraAuditorIds,
    requestId,
    fast,
    requiredMasterPubkey,
    minSucceededAt
  };

  const startTime = Date.now();
  let attemptCount = 0;
  let timeoutId = null;

  console.log('[pollForDecryptionKey] Starting polling:', {
    auditorIdentity,
    identity,
    interval,
    timeout,
    fast,
    requiredMasterPubkey: requiredMasterPubkey
      ? `${String(requiredMasterPubkey).substring(0, 20)}…`
      : null,
    minSucceededAt
  });

  return new Promise((resolve, reject) => {
    const finish = (fn, value) => {
      if (timeoutId) clearTimeout(timeoutId);
      fn(value);
    };

    const poll = async () => {
      if (abortSignal && abortSignal.aborted) {
        console.log('[pollForDecryptionKey] Polling aborted');
        finish(reject, new Error('Polling aborted'));
        return;
      }

      attemptCount++;
      const elapsed = Date.now() - startTime;

      if (elapsed >= timeout) {
        console.error('[pollForDecryptionKey] Timeout reached:', {
          attemptCount,
          elapsed: elapsed / 1000,
          timeout: timeout / 1000,
          auditorIdentity,
          identity
        });
        finish(reject, new Error(`Timeout: Decryption key not found after ${timeout / 1000} seconds`));
        return;
      }

      try {
        if (onPoll) {
          onPoll(attemptCount, elapsed);
        }

        console.log(`[pollForDecryptionKey] Poll attempt ${attemptCount} (elapsed: ${(elapsed / 1000).toFixed(1)}s):`, {
          auditorIdentity,
          identity
        });

        const keyData = await checkExistingKeyRequest(auditorIdentity, identity, lookupOptions);

        if (abortSignal && abortSignal.aborted) {
          finish(reject, new Error('Polling aborted'));
          return;
        }

        console.log(`[pollForDecryptionKey] Poll attempt ${attemptCount} result:`, {
          found: !!keyData,
          hasValue: !!(keyData && keyData.value),
          keyDataStructure: keyData ? {
            hasKey: !!keyData.key,
            hasValue: !!keyData.value,
            valueType: typeof keyData.value,
            valueKeys: keyData.value && typeof keyData.value === 'object' ? Object.keys(keyData.value) : 'not an object'
          } : null
        });

        if (keyData && keyData.value) {
          console.log('[pollForDecryptionKey] Key found! Stopping polling:', {
            attemptCount,
            elapsed: (Date.now() - startTime) / 1000,
            keyData
          });
          finish(resolve, keyData);
          return;
        }

        console.log(`[pollForDecryptionKey] Key not found yet, scheduling next poll in ${interval}ms`);
        timeoutId = setTimeout(poll, interval);
      } catch (error) {
        console.error(`[pollForDecryptionKey] Poll attempt ${attemptCount} failed:`, {
          error: error.message,
          stack: error.stack,
          auditorIdentity,
          identity
        });
        timeoutId = setTimeout(poll, interval);
      }
    };

    poll();

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        if (timeoutId) clearTimeout(timeoutId);
        reject(new Error('Polling aborted'));
      });
    }
  });
};

/**
 * Try to get a string key from an object (checks common field names)
 * @param {Object} obj - Object that might contain the key
 * @returns {string|null} The key if found, null otherwise
 */
function getKeyFromObject(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const keyFields = ['key', 'decryptionKey', 'privateKey', 'private_key', 'decryption_key'];
  for (const field of keyFields) {
    const val = obj[field];
    if (val != null && typeof val === 'string' && val.length > 0) return val;
  }
  return null;
}

/**
 * Best-effort walk of JSON to find any non-empty string whose key name (lowercase) contains "decryption" and "key".
 * Mirrors Go hasDecryptionKey logic.
 * @param {any} obj - Parsed object or array
 * @returns {string|null}
 */
function walkForDecryptionKeyString(obj) {
  if (obj == null) return null;
  if (typeof obj === 'object' && !Array.isArray(obj)) {
    for (const k of Object.keys(obj)) {
      const lk = k.toLowerCase();
      if (lk.includes('decryption') && lk.includes('key')) {
        const val = obj[k];
        if (typeof val === 'string' && val.trim() !== '') return val;
      }
      const found = walkForDecryptionKeyString(obj[k]);
      if (found) return found;
    }
    return null;
  }
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = walkForDecryptionKeyString(item);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Decode Firestore Bytes/ByteString or base64 string to UTF-8 string (for JSON parsing).
 * Handles: string (base64), { toBase64() }, toUint8Array(), _byteString, binaryString, Uint8Array, Buffer.
 * @param {*} raw
 * @returns {string|null}
 */
function decodeBytesToUtf8(raw) {
  if (!raw) return null;
  // Already a UTF-8 string (e.g. some SDKs expose .binaryString)
  if (typeof raw === 'string' && raw.length > 0) {
    if (raw.startsWith('{') || raw.startsWith('[')) return raw;
    try {
      if (typeof Buffer !== 'undefined') return Buffer.from(raw, 'base64').toString('utf8');
      return decodeURIComponent(escape(atob(raw)));
    } catch (e) {
      return null;
    }
  }
  // Firestore Bytes: toUint8Array() is reliable in browser
  if (typeof raw.toUint8Array === 'function') {
    try {
      const arr = raw.toUint8Array();
      if (arr && arr.length) return new TextDecoder().decode(arr);
    } catch (e) {
      // fall through
    }
  }
  if (typeof raw.toBase64 === 'function') {
    try {
      const b64 = raw.toBase64();
      if (typeof Buffer !== 'undefined') return Buffer.from(b64, 'base64').toString('utf8');
      return decodeURIComponent(escape(atob(b64)));
    } catch (e) {
      return null;
    }
  }
  // Internal _byteString (Uint8Array or string) - some SDKs
  if (raw._byteString != null) {
    const s = decodeBytesToUtf8(raw._byteString);
    if (s) return s;
  }
  if (raw.binaryString && typeof raw.binaryString === 'string') return raw.binaryString;
  if (raw instanceof Uint8Array || (typeof Buffer !== 'undefined' && Buffer.isBuffer(raw))) {
    try {
      if (typeof Buffer !== 'undefined') return Buffer.from(raw).toString('utf8');
      return new TextDecoder().decode(raw);
    } catch (e) {
      return null;
    }
  }
  // Iterable (e.g. some Bytes implementations)
  if (raw && typeof raw[Symbol.iterator] === 'function') {
    try {
      const arr = new Uint8Array(Array.from(raw));
      return new TextDecoder().decode(arr);
    } catch (e) {
      return null;
    }
  }
  if (raw.bytes && (raw.bytes instanceof Uint8Array || (typeof Buffer !== 'undefined' && Buffer.isBuffer(raw.bytes)))) {
    const buf = raw.bytes;
    if (typeof Buffer !== 'undefined') return Buffer.from(buf).toString('utf8');
    return new TextDecoder().decode(buf);
  }
  // Some SDKs expose raw UTF-8 string (e.g. binaryString in devtools)
  if (typeof raw === 'object') {
    for (const key of ['binaryString', '_binaryString', 'utf8', 'data']) {
      const v = raw[key];
      if (typeof v === 'string' && v.length > 10 && (v.startsWith('{') || v.startsWith('['))) return v;
    }
  }
  return null;
}

/**
 * Get dkRequestSuccess by decoding value or _byteString from keyRequestData or fullResult.
 * Handles Firestore Bytes/ByteString (value or _byteString may be Bytes type, not parsed JSON).
 * @param {Object} keyRequestData - result.data.value or similar
 * @param {Object} fullResult - result.data (has _byteString, value, etc.)
 * @returns {Object|null} dkRequestSuccess or null
 */
function getDkRequestSuccessFromPayload(keyRequestData, fullResult) {
  // Already parsed dkRequestSuccess (Go json tags: auditor_identity, chain_response, decryption_key)
  if (
    keyRequestData &&
    typeof keyRequestData === 'object' &&
    (keyRequestData.ChainResponse != null ||
      keyRequestData.chain_response != null ||
      keyRequestData.AuditorIdentity != null ||
      keyRequestData.auditor_identity != null ||
      keyRequestData.DecryptionKey != null ||
      keyRequestData.decryption_key != null)
  ) {
    return keyRequestData;
  }
  // Collect all candidate blobs to decode (value, value._byteString, fullResult._byteString)
  const candidates = [];
  if (fullResult && fullResult._byteString) candidates.push(fullResult._byteString);
  if (keyRequestData && typeof keyRequestData === 'object') {
    if (typeof keyRequestData.toBase64 === 'function' || typeof keyRequestData.toUint8Array === 'function' || keyRequestData instanceof Uint8Array) {
      candidates.push(keyRequestData);
    }
    if (keyRequestData._byteString != null && !candidates.includes(keyRequestData._byteString)) {
      candidates.push(keyRequestData._byteString);
    }
  }
  for (const toDecode of candidates) {
    const jsonStr = decodeBytesToUtf8(toDecode);
    if (jsonStr && jsonStr.startsWith('{')) {
      try {
        return JSON.parse(jsonStr);
      } catch (e) {
        // next candidate
      }
    }
  }
  return null;
}

/**
 * Get ChainResponse from dkRequestSuccess-style value (ChainResponse or chain_response), parsed if string.
 * @param {Object} keyRequestData
 * @returns {any} Parsed chain response object or null
 */
function getChainResponseObject(keyRequestData) {
  const raw = keyRequestData?.ChainResponse ?? keyRequestData?.chain_response;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  return raw;
}

/**
 * Extract decryption key from key request data
 * Handles dkRequestSuccess (ChainResponse / chain_response) and kv format. Uses hasDecryptionKey-style
 * walk to find any field whose name contains "decryption" and "key" with non-empty string value.
 * @param {Object} keyRequestData - The value field from Firestore document (or full result.data for kv)
 * @param {Object} options - Optional: { fullResult } to pass the full result.data for kv _byteString etc.
 * @returns {string|null} The decryption key (plain) or ciphertext string if found, null otherwise
 */
export const extractDecryptionKeyFromResponse = (keyRequestData, options = {}) => {
  console.log('[extractDecryptionKeyFromResponse] Extracting key from:', {
    hasData: !!keyRequestData,
    dataType: typeof keyRequestData,
    dataKeys: keyRequestData && typeof keyRequestData === 'object' ? Object.keys(keyRequestData) : 'not an object'
  });

  if (!keyRequestData) {
    console.log('[extractDecryptionKeyFromResponse] No data');
    return null;
  }

  // 0. Value is the key itself (string)
  if (typeof keyRequestData === 'string' && keyRequestData.length > 0) {
    console.log('[extractDecryptionKeyFromResponse] Value is string (key itself)');
    return keyRequestData;
  }

  // 1. dkRequestSuccess: decode value / _byteString (Firestore Bytes) then get decryption_key / chain_response
  const dkSuccess = getDkRequestSuccessFromPayload(keyRequestData, options.fullResult);
  if (dkSuccess) {
    // New format: decryption_keys is an array of VSS keyshares (each an ECIES ciphertext).
    // Return the first encrypted_keyshare_value so callers detect a ciphertext and route to
    // the auto-decrypt path, which decrypts every share and aggregates them.
    let dkArray = dkSuccess.DecryptionKeys ?? dkSuccess.decryption_keys;
    if (typeof dkArray === 'string') {
      try {
        dkArray = JSON.parse(dkArray);
      } catch (e) {
        dkArray = null;
      }
    }
    if (Array.isArray(dkArray)) {
      for (const share of dkArray) {
        const hexVal = share?.encrypted_keyshare_value ?? share?.EncryptedKeyshareValue;
        if (typeof hexVal === 'string' && hexVal.trim() !== '') {
          console.log('[extractDecryptionKeyFromResponse] Extracted from dkRequestSuccess.decryption_keys[]');
          return hexVal.trim();
        }
      }
    }

    const keyStr = dkSuccess.DecryptionKey ?? dkSuccess.decryption_key;
    if (keyStr != null && typeof keyStr === 'string' && keyStr.trim() !== '') {
      console.log('[extractDecryptionKeyFromResponse] Extracted from dkRequestSuccess.decryption_key');
      return keyStr.trim();
    }
    let chainResp = dkSuccess.ChainResponse ?? dkSuccess.chain_response;
    if (chainResp != null) {
      if (typeof chainResp === 'string') {
        try {
          chainResp = JSON.parse(chainResp);
        } catch (e) {
          chainResp = null;
        }
      }
      const shares = chainResp?.private_keyshares;
      if (Array.isArray(shares)) {
        for (const share of shares) {
          const hexVal = share?.encrypted_keyshare_value;
          if (typeof hexVal === 'string' && hexVal.trim() !== '') {
            console.log('[extractDecryptionKeyFromResponse] Extracted from chain_response.private_keyshares');
            return hexVal.trim();
          }
        }
      }
    }
  }

  // 2. Kv / top-level format: value.key, value.decryptionKey, etc.
  const topLevelKey = getKeyFromObject(keyRequestData);
  if (topLevelKey) {
    console.log('[extractDecryptionKeyFromResponse] Extracted from top-level key field');
    return topLevelKey;
  }

  // 3. Walk entire value (nested objects/arrays) for decryption-key-like field
  const byWalk = walkForDecryptionKeyString(keyRequestData);
  if (byWalk) {
    console.log('[extractDecryptionKeyFromResponse] Extracted from walk');
    return byWalk;
  }

  // 4. Single nested object (e.g. value = { someKey: { key: "..." } })
  if (typeof keyRequestData === 'object') {
    for (const key of Object.keys(keyRequestData)) {
      const nested = keyRequestData[key];
      const fromNested = getKeyFromObject(nested);
      if (fromNested) {
        console.log('[extractDecryptionKeyFromResponse] Extracted from nested object key:', key);
        return fromNested;
      }
    }
  }

  console.log('[extractDecryptionKeyFromResponse] No key found in any expected shape');
  return null;
};

/**
 * Heuristic: string that looks like ECIES ciphertext (long hex or base64) rather than a plain IBE key.
 * Use this to avoid treating ciphertext as the plain decryption key.
 * @param {string} str
 * @returns {boolean}
 */
export function isLikelyEciesCiphertext(str) {
  if (typeof str !== 'string' || str.length < 81) return false;
  const t = str.trim();
  if (t.startsWith('{') || t.startsWith('[')) return false;
  if (/^[0-9a-fA-F]+$/.test(t) || (t.startsWith('0x') && /^0x[0-9a-fA-F]+$/.test(t))) return true;
  try {
    const buf = Buffer.from(t, 'base64');
    return buf.length >= 81;
  } catch (e) {
    return false;
  }
}

/**
 * Request decryption key for an IBE identity (new flow).
 * First checks Firestore for existing key, then requests from API if not found.
 * Existing keys issued under a previous IBE master public key are treated as stale
 * after an on-chain master pubkey rotation and a new key is requested.
 * @param {string} identity - PEP / IBE identity (typically address.toLowerCase() + chain pk)
 * @param {string} secpPublicKey - Auditor's SECP256k1 public key from database
 * @param {string} auditorUid - Auditor's UID (used as auditor_identity for key requests and Firestore lookups)
 * @param {Object} [lookupHints] - Optional hints for `kv` reads (chain tx hash, grant id, etc.)
 * @param {string|null} [lookupHints.requiredMasterPubkey] - Active master pubkey (fetched if omitted)
 * @param {boolean} [lookupHints.forceNewRequest] - Skip reuse of any existing kv success entry
 * @param {string|null} [lookupHints.minSucceededAt] - Only reuse/poll keys newer than this ISO time
 * @param {boolean} [lookupHints.skipMasterPubkeyCheck] - Do not fetch/compare active master pubkey
 * @returns {Promise<Object>} Result object with existing key data or API response
 */
export const requestDecryptionKeyForTransaction = async (identity, secpPublicKey, auditorUid, lookupHints = {}) => {
  console.log('[requestDecryptionKeyForTransaction] Starting request:', {
    identity,
    auditorUid,
    hasPublicKey: !!secpPublicKey,
    publicKeyPreview: secpPublicKey ? secpPublicKey.substring(0, 30) + '...' : 'none',
    forceNewRequest: !!lookupHints.forceNewRequest,
    hasRequiredMasterPubkey: !!lookupHints.requiredMasterPubkey
  });

  if (!identity) {
    throw new Error('Identity is required');
  }
  
  if (!secpPublicKey) {
    throw new Error('Auditor SECP public key is required');
  }

  if (!auditorUid) {
    throw new Error('Auditor UID is required');
  }

  // Use auditor UID as identity (no derivation from public key)
  const auditorIdentity = auditorUid;

  const {
    forceNewRequest = false,
    skipMasterPubkeyCheck = false,
    ...restHints
  } = lookupHints;

  let requiredMasterPubkey = restHints.requiredMasterPubkey || null;
  if (!skipMasterPubkeyCheck && !requiredMasterPubkey) {
    requiredMasterPubkey = await fetchActiveMasterPubkey();
  }

  let minSucceededAt = restHints.minSucceededAt || null;

  // When forcing a refresh after a stale/failed key, ignore any existing success
  // entry that is not newer than what we already had.
  if (forceNewRequest && !minSucceededAt) {
    const staleExisting = await checkExistingKeyRequest(auditorIdentity, identity, {
      ...restHints,
      // Intentionally do not pass requiredMasterPubkey / minSucceededAt — we want the
      // raw current success entry so we can require something strictly newer.
      requiredMasterPubkey: null,
      minSucceededAt: null,
      fast: true
    });
    const prevSucceededAt = extractSucceededAtFromKvEntry(staleExisting);
    if (prevSucceededAt) {
      minSucceededAt = prevSucceededAt;
    } else {
      // No timestamp on the old entry — use "now" so only a newly written success matches.
      minSucceededAt = new Date().toISOString();
    }
    console.log('[requestDecryptionKeyForTransaction] Forcing new key request:', {
      minSucceededAt,
      hadExisting: !!staleExisting
    });
  }

  const effectiveHints = {
    ...restHints,
    requiredMasterPubkey,
    minSucceededAt
  };
  
  console.log('[requestDecryptionKeyForTransaction] Checking for existing key:', {
    auditorIdentity,
    identity,
    normalizedAuditor: auditorIdentity.toLowerCase(),
    normalizedIdentity: identity.toLowerCase(),
    requiredMasterPubkey: requiredMasterPubkey
      ? `${String(requiredMasterPubkey).substring(0, 20)}…`
      : null,
    minSucceededAt,
    forceNewRequest
  });
  
  // Check Firestore for existing key request (skipped entirely when forcing a brand-new request
  // without a minSucceededAt baseline — otherwise filtered check may still return stale data).
  if (!forceNewRequest) {
    const existingKey = await checkExistingKeyRequest(auditorIdentity, identity, effectiveHints);
    
    console.log('[requestDecryptionKeyForTransaction] Existing key check result:', {
      found: !!existingKey,
      hasValue: !!(existingKey && existingKey.value),
      existingKeyStructure: existingKey ? {
        hasKey: !!existingKey.key,
        hasValue: !!existingKey.value,
        valueType: typeof existingKey.value,
        storedMasterPubkey: extractMasterPubkeyFromKvEntry(existingKey)
          ? `${extractMasterPubkeyFromKvEntry(existingKey).substring(0, 20)}…`
          : null
      } : null
    });
    
    if (existingKey) {
      console.log('[requestDecryptionKeyForTransaction] Existing key found, returning it');
      return {
        exists: true,
        data: existingKey,
        masterPubkey: requiredMasterPubkey,
        minSucceededAt
      };
    }
  } else {
    console.log('[requestDecryptionKeyForTransaction] Skipping existing-key reuse (forceNewRequest)');
  }
  
  // Key doesn't exist (or is stale / force refresh), make API request
  console.log('[requestDecryptionKeyForTransaction] No usable existing key found, making API request');

  let apiResponse;
  try {
    apiResponse = await requestPrivateDecryptionKey(auditorIdentity, identity, secpPublicKey);
  } catch (apiError) {
    if (!isUnreadableResponseError(apiError)) {
      throw apiError;
    }
    // The request reaches the compliance server, but the browser discards the
    // response when it carries no CORS header, so `fetch` reports a network
    // failure for a call that actually succeeded and wrote the key to `kv`.
    // Reporting failure here is what made the first click fail and only the
    // second one work. Treat it as submitted and let the caller poll `kv`;
    // a genuine outage simply surfaces as a polling timeout instead.
    console.warn(
      '[requestDecryptionKeyForTransaction] Key request response unreadable ' +
      `(${apiError.message}). Request was likely delivered — polling kv for the key.`
    );
    return {
      exists: false,
      data: {},
      responseUnreadable: true,
      apiError: apiError.message,
      masterPubkey: requiredMasterPubkey,
      minSucceededAt
    };
  }

  console.log('[requestDecryptionKeyForTransaction] API request completed:', {
    hasTxHash: !!apiResponse.tx_hash,
    status: apiResponse.status,
    response: apiResponse
  });

  return {
    exists: false,
    data: apiResponse,
    masterPubkey: requiredMasterPubkey,
    minSucceededAt
  };
};
